import { useMemo } from "react";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { isOneTimePrice } from "@/react/helpers/paywallPriceHelpers";
import { customizedAppearance as appearance } from "../../StripeElements/stripeElementsOptions";
import {
  type StripeElementsMode,
  stripeElementsModes,
} from "../../StripeElements/types";
import { getPaywallPriceAmount } from "../helpers/paywallPrices";

export const useOptions = () => {
  const {
    currentCommunityProcessorPaymentMethodConfigurationId:
      paymentMethodConfiguration,
    currentCommunityMemberPaymentProcessorSessionClientSecret:
      customerSessionClientSecret,
    isPaywallsEuPaymentMethodsEnabled,
    paywall,
    getSelectedPrice,
    previewMutation,
  } = usePaywallCheckoutContext();

  const currency = paywall.currency.code;
  const selectedPrice = getSelectedPrice();
  const preview = previewMutation.data;

  const amount = getPaywallPriceAmount(selectedPrice, preview);

  // If the amount is 0 (either because it's free or trial), we won't confirm a payment intent
  // nor save the payment method. To guarantee that we save the payment method for future use when
  // required (e.g. paywall that requires a payment method for trial), we use the `setup` mode.
  const isAmountZero = amount === 0;
  const isSelectedPriceOneTime = isOneTimePrice(selectedPrice);

  let mode: StripeElementsMode;
  if (isAmountZero) {
    mode = stripeElementsModes.setup;
  } else if (isSelectedPriceOneTime || isPaywallsEuPaymentMethodsEnabled) {
    mode = stripeElementsModes.payment;
  } else {
    mode = stripeElementsModes.subscription;
  }

  return useMemo<StripeElementsOptions>(
    () => ({
      appearance,
      paymentMethodConfiguration,
      customerSessionClientSecret,
      mode,
      currency,
      amount,
    }),
    [
      paymentMethodConfiguration,
      customerSessionClientSecret,
      mode,
      currency,
      amount,
    ],
  );
};
